import React from 'react';
import { Search, Heart, GraduationCap, Star, Power, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { SortableHeader } from './AdminComponents';
import { getLocalizedText, formatPrice } from '../../utils/helpers';

export const ContentTab = ({ 
  allServices,
  allCourses,
  filteredServices,
  filteredCourses,
  contentSearch,
  setContentSearch,
  serviceFilter,
  setServiceFilter,
  courseFilter,
  setCourseFilter,
  contentSortConfig,
  onContentSort,
  onToggleFeatured,
  onToggleServiceActive,
  onToggleCourseActive,
  onDeleteService,
  onDeleteCourse
}) => {
  return (
    <div className="space-y-8">
      {/* Search and Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-[250px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar por nombre de servicio, curso o profesional..."
              value={contentSearch}
              onChange={(e) => setContentSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <Select value={serviceFilter} onValueChange={setServiceFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filtrar servicios" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los servicios</SelectItem>
            <SelectItem value="featured">Destacados</SelectItem>
            <SelectItem value="active">Activos</SelectItem>
            <SelectItem value="inactive">Inactivos</SelectItem>
          </SelectContent>
        </Select>
        <Select value={courseFilter} onValueChange={setCourseFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filtrar cursos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los cursos</SelectItem>
            <SelectItem value="featured">Destacados</SelectItem>
            <SelectItem value="active">Activos</SelectItem>
            <SelectItem value="inactive">Inactivos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Services Section */}
      <div>
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Heart className="h-5 w-5 text-rose-500" />
          Servicios ({filteredServices.length} de {allServices.length})
        </h3>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="Servicio" sortKey="title" currentSort={contentSortConfig} onSort={onContentSort} />
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Profesional</th>
                <SortableHeader label="Precio" sortKey="price" currentSort={contentSortConfig} onSort={onContentSort} />
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Estado</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredServices.map(service => (
                <tr key={service.id} className={`hover:bg-gray-50 ${!service.is_active ? 'bg-gray-100 opacity-60' : ''} ${service.is_featured || service.featured ? 'bg-yellow-50' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <img src={service.images?.[0] || '/default-service.png'} alt="" className="w-10 h-10 rounded-lg object-cover" />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm">{getLocalizedText(service.title, 'es')}</p>
                          {(service.is_featured || service.featured) && (
                            <Badge className="bg-yellow-500 text-xs">Destacado</Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{service.service_type}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">{service.teacher_name || service.user_name || '-'}</td>
                  <td className="px-4 py-3 text-sm">{formatPrice(service.price, service.currency)}</td>
                  <td className="px-4 py-3">
                    <Badge className={service.is_active ? 'bg-green-500' : 'bg-gray-500'}>
                      {service.is_active ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end">
                      <Button 
                        size="sm" 
                        variant="outline"
                        className={(service.is_featured || service.featured) ? 'text-yellow-600 bg-yellow-100' : ''}
                        onClick={() => onToggleFeatured('services', service.id, service.is_featured || service.featured)}
                        title={service.is_featured || service.featured ? 'Quitar destacado' : 'Destacar servicio'}
                      >
                        <Star className={`h-4 w-4 ${(service.is_featured || service.featured) ? 'fill-yellow-500' : ''}`} />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className={service.is_active ? 'text-orange-600' : 'text-green-600'}
                        onClick={() => onToggleServiceActive(service.id)}
                        title={service.is_active ? 'Desactivar' : 'Activar'}
                      >
                        <Power className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-red-600"
                        onClick={() => onDeleteService(service.id, getLocalizedText(service.title, 'es'))}
                        title="Eliminar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Courses Section */}
      <div>
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <GraduationCap className="h-5 w-5 text-emerald-500" />
          Cursos ({filteredCourses.length} de {allCourses.length})
        </h3>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="Curso" sortKey="title" currentSort={contentSortConfig} onSort={onContentSort} />
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Escuela</th>
                <SortableHeader label="Precio" sortKey="price" currentSort={contentSortConfig} onSort={onContentSort} />
                <SortableHeader label="Fecha" sortKey="start_date" currentSort={contentSortConfig} onSort={onContentSort} />
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Estado</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredCourses.map(course => (
                <tr key={course.id} className={`hover:bg-gray-50 ${!course.is_active ? 'bg-gray-100 opacity-60' : ''} ${course.is_featured || course.featured ? 'bg-yellow-50' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <img src={course.images?.[0] || '/default-course.png'} alt="" className="w-10 h-10 rounded-lg object-cover" />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm">{getLocalizedText(course.title, 'es')}</p>
                          {(course.is_featured || course.featured) && (
                            <Badge className="bg-yellow-500 text-xs">Destacado</Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{course.course_type}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">{course.school_name || course.user_name || '-'}</td>
                  <td className="px-4 py-3 text-sm">{formatPrice(course.price, course.currency)}</td>
                  <td className="px-4 py-3 text-sm">
                    {course.start_date ? new Date(course.start_date).toLocaleDateString('es-ES') : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={course.is_active ? 'bg-green-500' : 'bg-gray-500'}>
                      {course.is_active ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end">
                      <Button 
                        size="sm" 
                        variant="outline"
                        className={(course.is_featured || course.featured) ? 'text-yellow-600 bg-yellow-100' : ''}
                        onClick={() => onToggleFeatured('courses', course.id, course.is_featured || course.featured)}
                        title={course.is_featured || course.featured ? 'Quitar destacado' : 'Destacar curso'}
                      >
                        <Star className={`h-4 w-4 ${(course.is_featured || course.featured) ? 'fill-yellow-500' : ''}`} />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className={course.is_active ? 'text-orange-600' : 'text-green-600'}
                        onClick={() => onToggleCourseActive(course.id)}
                        title={course.is_active ? 'Desactivar' : 'Activar'}
                      >
                        <Power className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-red-600"
                        onClick={() => onDeleteCourse(course.id, getLocalizedText(course.title, 'es'))}
                        title="Eliminar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
